import { TEmailAPI, TEmailAPIFilters, TEmailAPIFiltersType, TEmailAPIRightHandMenu } from "./types"
import { Button, CircularProgress, ContentSectionContainer, DialogHandler, Dropdown, EEmailProviders, EEvents, EmailAPIFactory, EOperatorTypes, fetchRoute, FilterBar, FullBar, GenericWrapperContext, Header, Icon, requestSupportService, Search, SideMenu, SideMenuContainer, TFilterBarupdateReturn, TGenericListReturn, trackEvent, Typography } from "cakemail-ui-components-v2"
import Stack from "@mui/material/Stack"
import "./styles.scss"
import { useContext, useEffect, useRef, useState, MouseEvent } from "react"
import { useTranslation } from "react-i18next"
import { ERoutes } from "../../utils/url"
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom"
import { SelectChangeEvent } from "@mui/material/Select/SelectInput"
import { EmailAPIContext } from "./context"
import { getCurrentStoredFilters, setCurrentStoredFilters } from "../../utils/components"
import { TDropMenuItem } from "cakemail-ui-components-v2/dist/esm/components/dropMenu/types"
import { EmailAPIReports } from "./containers/reports"
import { LogInspect } from "../../components/emailAPIanalytics/logInspect"
import { EmailAPIMetrics } from "./containers/metrics"
import { EmailAPIRequestLogs } from "./containers/requestLogs"
import { EmailAPIDeliverability } from "./containers/deliverability"
import { EmailAPILandingPage } from "../../components/landingPages/emailAPI"
import { EmailReportContainer } from "../emailReport"
import { TButtonRef } from "cakemail-ui-components-v2/dist/esm/components/button/types"
import { EmailAPIBillingBlock } from "../../components/billing/emailAPI"
import { SideDrawerContainer } from "../../components/sideDrawerContainer"
import { TagFilterMenu } from "../../components/filtering/tagFilterMenu"
import { DateRangeDD } from "../../components/dateRangeDropdown"
import { openSupportDialog } from "../../components/dialogs/supportDialog"

export function EmailAPI({ initialTab }: TEmailAPI) {
    const genericWrapper = useContext(GenericWrapperContext)
    const location = useLocation()
    const subTab = useRef(initialTab || "home")
    const { t, i18n } = useTranslation()

    const filterStorageKey = "emailApiFilters"
    const initFilters: TEmailAPIFilters = getCurrentStoredFilters<TEmailAPIFilters>({ key: filterStorageKey, accountId: String(genericWrapper.account?.id) })

    //Filtering data
    const [tagsFilter, setTagsFilter] = useState<string[]>(initFilters?.tagsFilter || [])
    const [search, setSearch] = useState<string>(initFilters?.search || "")
    const [providerFilter, setProviderFilter] = useState<EEmailProviders[]>(initFilters?.providers || [])
    const [openedSearch, setOpenedSearch] = useState(!!initFilters?.search)
    const [timeRange, setTimeRange] = useState<string>(initFilters?.timeRange || "1d")
    const [filterOperator, setFilterOperator] = useState<EOperatorTypes>(initFilters?.filterOperator || EOperatorTypes.or)
    //todo re add when trend dropdown is back and working with graph
    const [trendRange, setTrendRange] = useState<number>(1 /*initFilters?.trendRange || 3*/)

    //List of email tags
    const [emailTags, setEmailTags] = useState<TGenericListReturn<string>>()

    //Right hand side menu. takes a string then ouputs component
    const [rightHandMenu, setRightHandMenu] = useState<TEmailAPIRightHandMenu>()
    // Takes a tagFilter to build an email report
    const [emailReportTagFilters, setEmailReportTagFilters] = useState<any>()

    //Content refresh data
    const [contentKeyId, setContentKeyId] = useState(0)
    const refreshTimeout = useRef<NodeJS.Timeout>()

    const apiPathName = location.pathname.split("/")[2]
    const apiPathSub = location.pathname.split("/")?.[3]

    useEffect(fetchEmailTags, [])
    useEffect(() => { return () => { clearTimeout(refreshTimeout.current) } }, [])

    function setFilterStorage({ ...filters }: Partial<TEmailAPIFilters> = {}) {
        setCurrentStoredFilters<TEmailAPIFilters>({
            key: filterStorageKey,
            accountId: Number(genericWrapper.account?.id),
            data: {
                timeRange: filters.timeRange || timeRange,
                tagsFilter: filters.tagsFilter || tagsFilter,
                search: filters.search || search,
                trendRange: filters.trendRange || trendRange,
                providers: filters.providers || providerFilter,
                filterOperator: filters.filterOperator || filterOperator,
            }
        })
    }

    function fetchEmailTags() {
        EmailAPIFactory.listTags({ perPage: 200 }).then((data) => {
            setEmailTags(data)
        })
    }

    function handleFilterBarChange({ filterTerms, operator }: TFilterBarupdateReturn) {
        let filters: any = {
            tags: [],
            providers: [],
            partial: []
        }

        filterTerms.forEach((term) => {
            filters[term.tab || "partial"].push(term.value)
        })

        setTagsFilter(filters.tags)
        setProviderFilter(filters.providers)
        setFilterOperator(operator)
        setSearch(filters.partial[0])

        setFilterStorage({
            timeRange: timeRange,
            tagsFilter: filters.tags,
            providers: filters.providers,
            filterOperator: operator,
            search: filters.partial[0]
        })
    }

    function handleRangeChange(event: SelectChangeEvent<string>) {
        const { name, value } = event.target
        setTimeRange(value)
        setFilterStorage({ timeRange: value })
    }

    function handleTrendChange(event: SelectChangeEvent<string>) {
        const { name, value } = event.target
        setTrendRange(Number(value))
        setFilterStorage({ trendRange: Number(value) })
    }

    function handleSetRightHandMenu(menuInfo?: TEmailAPIRightHandMenu) {
        setRightHandMenu(menuInfo)
    }

    function handleSetEmailReportTagFilters(filter?: any) {
        setEmailReportTagFilters(filter)
    }

    function handleSideNavClick(e: MouseEvent) {
        const data = e?.currentTarget?.getAttribute("data-name") || ""
        //To do, store these links in brand instead of hardcoding
        if (data === "doc-started") {
            window.open("https://cakemail.dev/api/reference/email-api#submit-an-email", "_blank")
        } else if (data === "doc-activity") {
            window.open("https://cakemail.dev/docs/getting-started-with-the-email-api", "_blank")
        }
        setSearch("")
        setOpenedSearch(false)
    }

    function handleOpenSupportDialog(exContext: string | undefined = undefined) {
        openSupportDialog({
            dialogTitle: t("landing-page-email-api-request-title"),
            dialogContent: t("landing-page-email-api-request-desc"),
            request: {
                subject: "Get Email API Access",
                message: "I want to check out email API"
            },
            eventTracking: {
                open: {
                    context: "EmailAPI Request Dialog Opened",
                    extendedContext: {
                        name: exContext
                    }
                },
                click: {
                    context: "EmailAPI Request Dialog Sent"
                },
                cancel: {
                    context: "EmailAPI Request Dialog Closed"
                }
            }
        })
    }

    function refreshContent(event: MouseEvent<HTMLButtonElement>, btnRef: TButtonRef) {
        if (!refreshTimeout.current) {
            setContentKeyId(contentKeyId + 1)
            clearTimeout(refreshTimeout.current)
            refreshTimeout.current = setTimeout(() => {
                refreshTimeout.current = undefined
            }, 5000)
        }
    }

    function handleCreateTagFilter() {
        setRightHandMenu({
            type: "tagFilter",
            id: ""
        })
    }

    const headingStringPart = apiPathSub ? `${apiPathName}-${apiPathSub}` : apiPathName

    const timeRangeEl = <DateRangeDD
        key="email-api-heading-timeRange"
        onChange={handleRangeChange}
        value={String(timeRange)}
    />

    const providers: TDropMenuItem[] = Object.values(EEmailProviders).map((p) => {
        return {
            text: p,
            value: p
        }
    })

    const otherProviderIndex = providers.findIndex(p => p.text === EEmailProviders.other)

    //For translation purpose
    if (otherProviderIndex !== -1) {
        providers[otherProviderIndex].text = t("email-log-type-other")
    }

    /* const trendRangeEl = <Dropdown
         key="email-api-heading-trend"
         color="body1"
         value={String(trendRange)}
         defaultValue="3"
         onChange={handleTrendChange}
         options={[
             {
                 text: t("general-x-trend", { count: 3 }),
                 value: "3"
             },
             {
                 text: t("general-x-trend", { count: 5 }),
                 value: "5"
             }
         ]}
     />*/

    const refeshEl = <Button key="email-api-heading-refresh" variant="outlined" color="body1" onClick={refreshContent}><Icon name="Refresh18" /></Button>

    const createEditReport = <Button key="email-api-heading-create-report" variant="outlined" color="body1" onClick={handleCreateTagFilter}><Icon name="Add18" /></Button>

    let rightMenu: any[] = []
    switch (apiPathName) {
        case "metrics":
            rightMenu = [
                refeshEl,
                timeRangeEl,
            ]
            break
        case "deliverability":
            rightMenu = [
                refeshEl,
                timeRangeEl,
            ]
            break
        case "reports":
            rightMenu = [
                refeshEl,
                timeRangeEl,
                createEditReport,
            ]
            break
        case "requestLog":
            rightMenu = [
                refeshEl,
                timeRangeEl,
            ]
            break
        case "doc":
            break
    }

    const filterBar = <FilterBar
        onUpdate={handleFilterBarChange}
        operatorOptions={[
            {
                text: t("filter-bar-condition-match-any"),
                value: "or"
            },
            {
                text: t("filter-bar-condition-match-all"),
                value: "and"
            }
        ]}
        addFilterTxt={t("filter-bar-add-filter")}
        saveTxt={t("general-save")}
        clearTxt={t("general-clear")}
        searchPlaceHolder={t("email-api-filter-bar-search-placeholder")}
        dropMenuOptions={[
            ...emailTags?.data?.map((t) => {
                return { tab: "tags", text: t, value: t }
            }) || [],
            ...Object.values(EEmailProviders).map((p) => {
                return { tab: "providers", text: p, value: p }
            })
        ]}
        dropMenuProps={{
            emptyTexts: [
                { tab: "tags", desc: t("filter-bar-no-match-email"), text: t("filter-bar-no-match-title") },
                { tab: "providers", desc: t("filter-bar-no-match-email"), text: t("filter-bar-no-match-title") },
            ],
            tabs: [
                { name: "tags", text: t("general-tags") },
                { name: "providers", text: t("general-provider_other") },
            ],
            disabledTabs: apiPathName !== "requestLog" ? ["partial"] : []
        }}

        initFilterTerms={[
            ...tagsFilter?.map((t) => {
                return { tab: "tags", text: t, value: t }
            }) || [],
            ...providerFilter?.map((p) => {
                return { tab: "providers", text: p, value: p }
            }) || [],
        ]}
        replacePartial
        //filterTerms={[]}
        //onSave={}
        //initOperatorValue=""
        operatorValue={filterOperator}

    />

    const hasAccessToFeature = genericWrapper.account?.usage_limits?.use_email_api

    return <Stack direction="row" className="emailAPIContainer">
        {!hasAccessToFeature &&
            <EmailAPILandingPage mainCTA={handleOpenSupportDialog} />
        }
        {hasAccessToFeature &&
            <>
                {!rightHandMenu &&
                    <SideMenu


                    >
                        <SideMenuContainer
                            menuItemClick={(e) => handleSideNavClick(e)}
                            menuItems={[
                                {
                                    text: t("email-api-header-api-heading"),
                                    variant: "section",
                                    name: "api-heading",
                                },
                                {
                                    name: "metrics",
                                    path: "#" + fetchRoute({ route: ERoutes.emailMetrics }),
                                    text: t("email-api-header-metrics-heading"),
                                    icon: "LineChart18",
                                    defaultItemIcon: false,
                                },
                                {
                                    name: "deliverability",
                                    path: "#" + fetchRoute({ route: ERoutes.emailDeliverability }),
                                    text: t("email-api-header-deliverability-heading"),
                                    icon: "Bounce18",
                                    defaultItemIcon: false,
                                },
                                /*{
                                    name: "reports",
                                    path: "#" + fetchRoute({ route: ERoutes.emailReports }),
                                    text: t("email-api-header-reports-heading"),
                                    icon: "Sends24",
                                },*/
                                {
                                    name: "requestLogs",
                                    path: "#" + fetchRoute({ route: ERoutes.emailRequestLog }),
                                    text: t("email-api-header-requestLog-heading"),
                                    icon: "Activity18",
                                    defaultItemIcon: false,
                                },
                                {
                                    name: "doc-started",
                                    // path: "#" + fetchRoute({ route: ERoutes.apiGettingStarted }),
                                    text: t("email-api-header-doc-getting-started-heading"),
                                    icon: "Publish18",
                                    defaultItemIcon: "Link10"
                                },
                                {
                                    name: "doc-activity",
                                    // path: "#" + fetchRoute({ route: ERoutes.apiReferences }),
                                    text: t("email-api-header-doc-references-heading"),
                                    icon: "Book18",
                                    defaultItemIcon: "Link10"
                                },
                            ]}
                        />
                        <EmailAPIBillingBlock />
                    </SideMenu>
                }

                <EmailAPIContext.Provider value={{
                    timeRange: timeRange,
                    trendRange: Number(trendRange),
                    emailTags,
                    tagsFilter,
                    search,
                    rightHandMenu,
                    setRightHandMenu: handleSetRightHandMenu,
                    providerFilter,
                    filterOperator,
                    setEmailReportTagFilters: handleSetEmailReportTagFilters,
                    refreshKey: contentKeyId
                }}>
                    <ContentSectionContainer>
                        <FullBar
                            left={<Header
                                headingVariant="titleL"
                                heading={t(`email-api-header-${headingStringPart}-heading`)}
                                headingTooltip={t(`email-api-header-${headingStringPart}-heading-tooltip`)}
                                support={t(`email-api-header-api-heading`)}
                            />}
                            right={rightMenu}
                        />

                        {filterBar}

                        <Stack className="subContentSectionContainer">
                            <Routes>
                                <Route path={fetchRoute({ route: ERoutes.emailMetrics })} element={<EmailAPIMetrics />} />
                                <Route path={fetchRoute({ route: ERoutes.emailDeliverability })} element={<EmailAPIDeliverability />} />
                                <Route path={fetchRoute({ route: ERoutes.emailReports })} element={<EmailAPIReports />} />
                                <Route path={fetchRoute({ route: ERoutes.emailRequestLog })} element={<EmailAPIRequestLogs />} />

                                <Route path={fetchRoute({ route: ERoutes.apiGettingStarted })} element={<h1>Getting started</h1>} />
                                <Route path={fetchRoute({ route: ERoutes.apiReferences })} element={<h1>References</h1>} />

                                <Route index path={"metrics"} element={<EmailAPIMetrics />} />
                                <Route path={"deliverability"} element={<EmailAPIDeliverability />} />
                                <Route path={"reports"} element={<EmailAPIReports />} />
                                <Route path={"requestLog"} element={<EmailAPIRequestLogs />} />


                                <Route path={"doc/getting-started"} element={<h1>Getting Started</h1>} />
                                <Route path={"doc/guide-reference"} element={<h1>Guide & Reference</h1>} />

                                <Route
                                    path="*"
                                    element={<Navigate to={fetchRoute({ route: ERoutes.emailMetrics })} replace />}
                                />

                            </Routes>
                        </Stack>
                    </ContentSectionContainer>


                    {rightHandMenu && <SideDrawerContainer>
                        {rightHandMenu.type === "logInspect" &&
                            <LogInspect emailId={rightHandMenu.id} refreshKey={contentKeyId} />
                        }

                        {rightHandMenu.type === "tagFilter" &&
                            <TagFilterMenu
                                metadataName="emailAPICustomReports"
                                onClose={() => { handleSetRightHandMenu() }}
                                initTags={emailTags}
                                initCustomReport={undefined}
                            />
                        }

                    </SideDrawerContainer>}
                    {emailReportTagFilters && <EmailReportContainer onClose={() => { setEmailReportTagFilters(undefined) }} tagFilter={emailReportTagFilters} />}
                </EmailAPIContext.Provider>
            </>
        }
    </Stack>
}